import { FilesetResolver, ImageSegmenter } from "@mediapipe/tasks-vision";

const FPS = 24;
let segP = null;
const getSeg = () => (segP ||= FilesetResolver.forVisionTasks("/mediapipe")
  .then(f => ImageSegmenter.createFromOptions(f, { baseOptions: { modelAssetPath: "/mediapipe/selfie_segmenter.tflite", delegate: "GPU" }, runningMode: "VIDEO", outputConfidenceMasks: true, outputCategoryMask: false }))
  .catch(e => { segP = null; throw e; }));
const imgs = {};
const loadImg = (src) => (imgs[src] ||= new Promise((ok, bad) => { const i = new Image(); i.crossOrigin = "use-credentials"; i.onload = () => ok(i); i.onerror = () => { delete imgs[src]; bad(new Error("image")); }; i.src = src; }));
const canvas = (w, h) => Object.assign(document.createElement("canvas"), { width: w, height: h });
const cover = (ctx, img, W, H) => { const s = Math.max(W / img.width, H / img.height), w = img.width * s, h = img.height * s; ctx.drawImage(img, (W - w) / 2, (H - h) / 2, w, h); };

/* Draws the camera into a canvas with optional blur/background replacement (follows phone rotation); returns a sendable video track. The logo is overlaid on every tile instead. */
export function createFx(raw) {
  const size = (vw, vh) => { const k = Math.min(1, 960 / Math.max(vw, vh)); return [Math.round(vw * k), Math.round(vh * k)]; };
  const st = raw.getSettings();
  let [W, H] = size(st.width || 1280, st.height || 720);
  const video = Object.assign(document.createElement("video"), { muted: true, playsInline: true, srcObject: new MediaStream([raw]) });
  video.play().catch(() => {});
  const out = canvas(W, H), ctx = out.getContext("2d");
  const person = canvas(W, H), pctx = person.getContext("2d");
  const mask = canvas(1, 1), mctx = mask.getContext("2d");
  const small = canvas(Math.max(1, Math.round(W / 14)), Math.max(1, Math.round(H / 14))), sctx = small.getContext("2d");
  const mid = canvas(Math.round(W / 5), Math.round(H / 5)), midctx = mid.getContext("2d");
  const fit = () => {
    const vw = video.videoWidth, vh = video.videoHeight;
    if (!vw || !vh) return;
    const [w, h] = size(vw, vh);
    if (w === W && h === H) return;
    W = w; H = h;
    out.width = person.width = W; out.height = person.height = H;
    small.width = Math.max(1, Math.round(W / 14)); small.height = Math.max(1, Math.round(H / 14));
    mid.width = Math.round(W / 5); mid.height = Math.round(H / 5);
  };
  const blurInto = (strong) => {
    const c = strong ? small : mid, cx = strong ? sctx : midctx;
    cx.imageSmoothingEnabled = true; cx.imageSmoothingQuality = "high";
    cx.drawImage(video, 0, 0, c.width, c.height);
    if (strong) { midctx.drawImage(small, 0, 0, mid.width, mid.height); ctx.drawImage(mid, 0, 0, W, H); } else ctx.drawImage(mid, 0, 0, W, H);
  };
  let mode = { type: "none" }, bg = null, seg = null, img = null, stopped = false, ts = 0;
  const track = out.captureStream(FPS).getVideoTracks()[0];
  const draw = () => {
    if (video.readyState < 2) return;
    fit();
    const fx = mode.type !== "none" && seg && (mode.type !== "image" || bg);
    if (!fx) ctx.drawImage(video, 0, 0, W, H);
    else {
      ts = Math.max(ts + 1, performance.now());
      const r = seg.segmentForVideo(video, ts);
      const masks = r.confidenceMasks || [];
      const m = masks[masks.length - 1];
      if (m) {
        if (mask.width !== m.width || mask.height !== m.height) { mask.width = m.width; mask.height = m.height; img = mctx.createImageData(m.width, m.height); }
        const a = m.getAsFloat32Array();
        for (let i = 0; i < a.length; i++) { const v = Math.min(1, Math.max(0, (a[i] - 0.2) / 0.4)); img.data[i * 4 + 3] = v * v * (3 - 2 * v) * 255; }
        mctx.putImageData(img, 0, 0);
      }
      r.close?.();
      ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = "high";
      if (mode.type === "image") cover(ctx, bg, W, H);
      else blurInto(mode.type === "blur-strong");
      pctx.imageSmoothingEnabled = true; pctx.imageSmoothingQuality = "high";
      pctx.globalCompositeOperation = "copy"; pctx.drawImage(video, 0, 0, W, H);
      pctx.globalCompositeOperation = "destination-in"; pctx.drawImage(mask, 0, 0, W, H);
      pctx.globalCompositeOperation = "source-over";
      ctx.drawImage(person, 0, 0);
    }
  };
  const loop = () => {
    if (stopped) return;
    const t0 = performance.now();
    try { draw(); } catch (e) { console.warn("fx", e); mode = { type: "none" }; }
    setTimeout(loop, Math.max(0, 1000 / FPS - (performance.now() - t0)));
  };
  loop();
  return {
    track,
    async setMode(m) {
      if (m.type !== "none") seg = seg || await getSeg();
      bg = m.type === "image" ? await loadImg(m.url) : null;
      mode = m;
    },
    async setLogo() {},
    stop() { stopped = true; track.stop(); video.srcObject = null; },
  };
}
